import { colors } from '@hero/web-theme';
import { SVGProps } from 'react';
export type IconVariant = 'primary' | 'secondary' | 'info' | 'default';
export type IconSize = 'small' | 'medium' | 'large';

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
  /** the size of the icon `small`, `medium`, `large`
   *
   * default is `small`
   */
  size?: IconSize;
  /** the color variant of the icon */
  variant?: IconVariant;
}

const SIZE_MAP: Record<IconSize, number> = {
  small: 16,
  medium: 24,
  large: 32,
} as const;

// default icon props
export const ICON = {
  size: 'small' as IconSize,
  variant: 'default' as IconVariant,
  strokeWidth: 1.5, // default stroke width
} as const;

const getIconSize = (size = ICON.size): number => {
  return SIZE_MAP[size];
};

const getVariantColor = (variant = ICON.variant): string => {
  switch (variant) {
    case 'primary':
      return colors.icons.primary;
    case 'secondary':
      return colors.icons.secondary;
    case 'info':
      return colors.icons.info;
    default:
      return 'currentColor';
  }
};

export const getIconProps = (props: IconProps = {}): React.SVGProps<SVGSVGElement> => {
  const { size, variant, strokeWidth, style, className, ...rest } = props;

  const iconSize = getIconSize(props.size);
  const strokeColor = getVariantColor(props.variant);

  // Check if className contains text-* color classes
  const hasTextColorClass = className && /text-\w+/.test(className);

  return {
    // Only set width/height if className is not provided (let className handle it if present)
    ...(!className && {
      width: iconSize,
      height: iconSize,
    }),
    // Always set stroke to ensure icons are visible
    // Use currentColor when className is provided, otherwise use variant color
    stroke: className ? 'currentColor' : strokeColor,
    strokeWidth: strokeWidth ?? ICON.strokeWidth,
    fill: 'none', // Default for stroke-based icons

    // Use style to ensure color cascades to child elements (needed for SVGR icon mode)
    // Always set color in style when variant is provided, unless className has text-* color
    style: {
      ...(!hasTextColorClass && { color: strokeColor }),
      ...style,
    },
    className,
    ...rest,
  };
};
